import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  CountryDto,
  LanguageDto,
  TenantSettingsDto,
  UpdateTenantSettingsDto,
} from '@splitsecondconnect/ultramarine-shared';
import { SettingsService } from '../services/settings.service';
import { finalize, forkJoin, tap } from 'rxjs';
import { SscSelectOption } from '@marineform/ssc-ui';

interface SettingsState {
  isCountriesLoading: boolean;
  isLanguagesLoading: boolean;
  isTenantSettingsLoading: boolean;
  isTenantSettingsSaving: boolean;
  countries: CountryDto[];
  languages: LanguageDto[];
  tenantSettings?: TenantSettingsDto;
}

const initialState: SettingsState = {
  isCountriesLoading: true,
  isLanguagesLoading: true,
  isTenantSettingsLoading: true,
  isTenantSettingsSaving: false,
  countries: [],
  tenantSettings: undefined,
  languages: [],
};

export const SettingsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    countrySelectOptions: computed((): SscSelectOption<number>[] =>
      store.countries().map((c) => ({
        value: c.id,
        label: c.name,
      })),
    ),
    currencyIsoCode: computed((): string | undefined => {
      return store.tenantSettings?.()?.currencyIsoCode ?? undefined;
    }),
    paymentProvidersIntegrationEnabled: computed((): boolean | undefined => {
      const isEnabled =
        store.tenantSettings?.()?.integrations?.paymentProviders?.isEnabled;
      return isEnabled == null ? undefined : !!isEnabled;
    }),
    languageSelectOptions: computed((): SscSelectOption<number>[] =>
      store
        .languages()
        .filter((language) => language.isVisible)
        .map((language) => ({
          value: language.id,
          label: language.name,
        })),
    ),
  })),
  withMethods((store, service = inject(SettingsService)) => {
      return {
        getSettings() {
          patchState(store, { isTenantSettingsLoading: true });
          return service.getSettings().pipe(
            tap((res) => {
              patchState(store, { tenantSettings: res.data });
            }),
            finalize(() =>
              patchState(store, { isTenantSettingsLoading: false }),
            ),
          );
        },
        getCountries() {
          patchState(store, { isCountriesLoading: true });
          return service.getCountries().pipe(
            tap((res) => {
              patchState(store, { countries: res.data });
            }),
            finalize(() => patchState(store, { isCountriesLoading: false })),
          );
        },
        getLanguages() {
          patchState(store, { isLanguagesLoading: true });
          return service.getLanguages().pipe(
            tap((res) => {
              patchState(store, { languages: res.data });
            }),
            finalize(() => patchState(store, { isLanguagesLoading: false })),
          );
        },
        getAll() {
          patchState(store, {
            isTenantSettingsLoading: true,
            isCountriesLoading: true,
            isLanguagesLoading: true,
          });
          return forkJoin({
            settings: this.getSettings(),
            countries: this.getCountries(),
            languages: this.getLanguages(),
          }).pipe(
            tap((res) => {
              const settings = res.settings.data;
              const languages = res.languages.data ?? [];
              patchState(store, {
                tenantSettings: settings,
                countries: res.countries.data ?? [],
                languages,
              });
            }),
            finalize(() =>
              patchState(store, {
                isTenantSettingsLoading: false,
                isCountriesLoading: false,
                isLanguagesLoading: false,
              }),
            ),
          );
        },
        updateSettings(record: Partial<UpdateTenantSettingsDto>) {
          patchState(store, { isTenantSettingsSaving: true });
          return service.updateSettings(record).pipe(
            tap((res) => {
              patchState(store, { tenantSettings: res.data });
            }),
            finalize(() =>
              patchState(store, { isTenantSettingsSaving: false }),
            ),
          );
        },
      };
    },
  ),
);
